import { catalogByEvent, GA, type GaAction, type GaParams } from './events';
import { pageName } from './pageName';

const SCRIPT_ID = 'hf-ga';
const CLICK_SELECTOR = 'button, a, [role="button"], [role="menuitem"], [role="tab"], input[type="submit"]';
const ITEM_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,79}$/;
const QUEUED_EVENT_CAP = 20;

export type AnalyticsUser = {
  sub: string;
  role?: string;
  companyKey?: string;
  datasource?: string;
  source?: string;
};

type Runtime = {
  ready: boolean;
  measurementId: string;
  environment: string;
  itemName?: string;
  lastPath?: string;
};

const runtime: Runtime = {
  ready: false,
  measurementId: '',
  environment: '',
};

const queued: Array<{ action: GaAction; params?: GaParams }> = [];

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function gaDisableKey(measurementId: string): string {
  return `ga-disable-${measurementId}`;
}

function setGaDisabled(measurementId: string, disabled: boolean): void {
  Object.assign(window, { [gaDisableKey(measurementId)]: disabled });
}

function readMeasurementId(): string {
  const raw: unknown = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (typeof raw !== 'string') return '';
  const id = raw.trim();
  return /^G-[A-Z0-9]+$/i.test(id) ? id : '';
}

function readEnvironment(): string {
  const raw: unknown = import.meta.env.VITE_HF_ENV;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : '';
}

function send(command: string, ...args: unknown[]): void {
  window.gtag?.(command, ...args);
}

/** Official gtag snippet — the library only replays `Arguments` objects, not rest-parameter arrays. */
function ensureGtagStub(): void {
  window.dataLayer = window.dataLayer ?? [];
  if (window.gtag) return;
  window.gtag = function gtag() {
    // Official snippet: gtag.js only replays Arguments objects, not rest-parameter arrays.
    // eslint-disable-next-line prefer-rest-params -- dataLayer queue format
    window.dataLayer.push(arguments);
  };
}

function configureMeasurement(): void {
  send('consent', 'update', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  send('js', new Date());
  send('config', runtime.measurementId, {
    send_page_view: false,
    allow_google_signals: false,
    anonymize_ip: true,
    page_title: pageName(currentPathname()),
    ...(runtime.environment === 'dev' ? { debug_mode: true } : {}),
  });
}

function isGaCookie(name: string): boolean {
  return name === '_ga' || name === '_gid' || name === '_gat' || name.startsWith('_ga_');
}

function expireGaCookies(): void {
  if (typeof document === 'undefined') return;
  const host = typeof location === 'undefined' ? '' : location.hostname;
  const domains = ['', ...(host ? [host, `.${host}`] : [])];
  const secure = typeof location !== 'undefined' && location.protocol === 'https:';
  const sameSites = ['', 'SameSite=Lax', 'SameSite=Strict', 'SameSite=None'];
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0';
  for (const part of document.cookie.split(';')) {
    const name = part.split('=')[0]?.trim() ?? '';
    if (!isGaCookie(name)) continue;
    for (const domain of domains) {
      const domainPart = domain ? `; domain=${domain}` : '';
      for (const sameSite of sameSites) {
        const sameSitePart = sameSite ? `; ${sameSite}` : '';
        const variants = sameSite === 'SameSite=None' || secure ? [true, false] : [false];
        for (const withSecure of variants) {
          const securePart = withSecure ? '; Secure' : '';
          document.cookie = `${name}=; ${expired}; path=/${domainPart}${sameSitePart}${securePart}`;
        }
      }
    }
  }
}

function pageLocation(pathname: string): string {
  if (typeof location === 'undefined') return pathname;
  return `${location.origin}${pathname}`;
}

export function isAnalyticsConfigured(): boolean {
  return Boolean(readMeasurementId());
}

export function isAnalyticsEnabled(): boolean {
  return runtime.ready;
}

export function initAnalytics(): void {
  if (runtime.ready || typeof document === 'undefined') return;
  const measurementId = readMeasurementId();
  if (!measurementId) return;

  runtime.measurementId = measurementId;
  runtime.environment = readEnvironment();
  setGaDisabled(measurementId, false);
  ensureGtagStub();
  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.onload = () => {
      if (!runtime.ready) return;
      configureMeasurement();
    };
    document.head.appendChild(script);
  }
  configureMeasurement();
  runtime.ready = true;
  flushQueued();
}

export function disableAnalytics(): void {
  const wasReady = runtime.ready;
  if (wasReady) {
    clearUser();
    queued.length = 0;
  }
  const measurementId = runtime.measurementId || readMeasurementId();
  if (measurementId) setGaDisabled(measurementId, true);
  if (typeof window.gtag === 'function') {
    send('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  }
  runtime.ready = false;
  runtime.lastPath = undefined;
  runtime.itemName = undefined;
  expireGaCookies();
}

export function identifyUser(user: AnalyticsUser): void {
  if (!runtime.ready) return;
  send('set', 'user_id', user.sub);
  send('set', 'user_properties', {
    user_role: user.role ?? 'user',
    account_type: user.source === 'internal' ? 'partner' : 'hedgefacts',
    datasource: user.datasource ?? '',
    company_id: user.companyKey ?? '',
    environment: runtime.environment,
  });
}

export function clearUser(): void {
  if (!runtime.ready) return;
  send('set', 'user_id', null);
  send('set', 'user_properties', {
    user_role: '',
    account_type: '',
    datasource: '',
    company_id: '',
    environment: runtime.environment,
  });
}

export function setAnalyticsItem(itemName?: string): void {
  runtime.itemName = itemName?.trim() || undefined;
}

function currentPathname(): string {
  if (runtime.lastPath) return runtime.lastPath;
  return typeof location === 'undefined' ? '/' : location.pathname;
}

/** GA4 Pages uses `document.title` when `page_title` is missing — keep them the same. */
export function syncDocumentTitle(pathname: string): void {
  if (typeof document === 'undefined') return;
  document.title = pageName(pathname);
}

function commonParams(pathname = currentPathname()): Record<string, string> {
  const title = pageName(pathname);
  const params: Record<string, string> = {
    page_name: title,
    page_title: title,
  };
  if (runtime.environment) params.environment = runtime.environment;
  if (runtime.itemName) params.item_id = runtime.itemName;
  return params;
}

export function trackPageView(pathname: string): void {
  if (!runtime.ready) return;
  if (runtime.lastPath === pathname) return;
  runtime.lastPath = pathname;
  const title = pageName(pathname);
  syncDocumentTitle(pathname);
  send('event', 'page_view', {
    ...commonParams(pathname),
    page_title: title,
    page_location: pageLocation(pathname),
    page_path: pathname,
    send_to: runtime.measurementId,
    action: `Viewed ${title}`,
  });
}

function sendEvent(action: GaAction, params?: GaParams): void {
  const extras = Object.fromEntries(Object.entries(params ?? {}).filter(([, value]) => value != null && value !== '')) as Record<string, string>;
  send('event', action.event, {
    ...commonParams(),
    ...extras,
    send_to: runtime.measurementId,
    action: action.action,
  });
}

function flushQueued(): void {
  const pending = queued.splice(0);
  for (const item of pending) sendEvent(item.action, item.params);
}

export function track(action: GaAction, params?: GaParams): void {
  if (runtime.ready) {
    sendEvent(action, params);
    return;
  }
  if (!readMeasurementId()) return;
  if (queued.length >= QUEUED_EVENT_CAP) queued.shift();
  queued.push({ action, params });
}

function clickTarget(node: EventTarget | null): Element | null {
  if (!(node instanceof Element)) return null;
  const target = node.closest(CLICK_SELECTOR);
  if (!target || target.closest('[data-ga-ignore]')) return null;
  if ('disabled' in target && (target as HTMLButtonElement).disabled) return null;
  return target;
}

function clickItemId(target: Element): string | undefined {
  const raw = target.getAttribute('data-ga-item')?.trim();
  return raw && ITEM_ID_RE.test(raw) ? raw : undefined;
}

export function handleAnalyticsClick(event: Event): void {
  if (!runtime.ready) return;
  const target = clickTarget(event.target);
  if (!target) return;
  const catalogEvent = target.getAttribute('data-ga-action');
  const catalog = catalogEvent ? catalogByEvent(catalogEvent) : undefined;
  const itemId = clickItemId(target);
  if (catalog) {
    track(catalog, itemId ? { item_id: itemId } : undefined);
    return;
  }
  if (!itemId) return;
  track(GA.clicked, { item_id: itemId });
}

export function listenClicks(): () => void {
  document.addEventListener('click', handleAnalyticsClick, true);
  return () => document.removeEventListener('click', handleAnalyticsClick, true);
}

export function resetAnalyticsForTests(): void {
  runtime.ready = false;
  runtime.measurementId = '';
  runtime.environment = '';
  runtime.itemName = undefined;
  runtime.lastPath = undefined;
  queued.length = 0;
}
